/**
 * The cashier's shift.
 *
 * Nothing can be sold until one is open. That is not a formality: the shift is
 * what every sale is attached to, and what the cash drawer is counted against
 * at the end. An order with no shift has no till to belong to and cannot be
 * reconciled — so the screen refuses to sell rather than allowing it and
 * leaving someone to untangle it later.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

export function useShift() {
  const client = useQueryClient();

  const query = useQuery({
    queryKey: ['shift', 'active'],
    queryFn: () => api.get('/shifts/active'),
    // Whether a shift is open changes rarely, and the answer is needed
    // instantly on every screen change.
    staleTime: 60_000,
  });

  const open = useMutation({
    mutationFn: (body) => api.post('/shifts/open', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['shift'] }),
  });

  const close = useMutation({
    mutationFn: (body) => api.post('/shifts/close', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['shift'] }),
  });

  return {
    shift: query.data?.active ? query.data : null,
    isOpen: Boolean(query.data?.active),
    // The branch has one shift; whether this person opened it, and whether they may close it.
    mine: Boolean(query.data?.shift?.mine),
    canClose: Boolean(query.data?.shift?.canClose),
    cashierName: query.data?.shift?.cashierName ?? null,
    loading: query.isLoading,
    error: query.error,
    openShift: open,
    closeShift: close,
  };
}
